/*!
 * Link Click tracking for Snowplow v4.5.0 (http://bit.ly/sp-js)
 * Copyright 2022 Snowplow Analytics Ltd, 2010 Anthon Pang
 * Licensed under BSD-3-Clause
 */

import { dispatchToTrackersInCollection, flushPendingCookies, getHostName, getCssClasses, getFilterByClass, addEventListener } from '@snowplow/browser-tracker-core';
import { buildLinkClick, resolveDynamicContext } from '@snowplow/tracker-core';

/*
 * Copyright (c) 2022 Snowplow Analytics Ltd, 2010 Anthon Pang
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 *    list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 *    this list of conditions and the following disclaimer in the documentation
 *    and/or other materials provided with the distribution.
 *
 * 3. Neither the name of the copyright holder nor the names of its
 *    contributors may be used to endorse or promote products derived from
 *    this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 * AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
 * IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE
 * FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL
 * DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
 * SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY,
 * OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
 * OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
var TRACKABLE_ELEMENTS = ['a', 'area']; // should be kept in sync with TrackableElement type
var TRACKABLE_ELEMENTS_SELECTOR = TRACKABLE_ELEMENTS.join(', ');
var ELEMENT_PROTOCOL_FILTER = /^(javascript|vbscript|jscript|mocha|livescript|ecmascript):/i;
var _trackers = {};
var _listeners = {};
var _configuration = {};
var _logger = undefined;
/**
 * Link click tracking.
 *
 * Will automatically track link clicks once enabled with `enableLinkClickTracking`
 * or you can manually track link clicks with `trackLinkClick`.
 *
 * @returns Plugin instance
 */
function LinkClickTrackingPlugin() {
    return {
        activateBrowserPlugin: function (tracker) {
            _trackers[tracker.id] = tracker;
        },
        logger: function (logger) {
            _logger = logger;
        },
    };
}
/**
 * Enable link click tracking.
 *
 * @remarks
 * The default behaviour is to use actual click events. However, some browsers
 * (e.g., Firefox, Opera, and Konqueror) don't generate click events for the middle mouse button.
 *
 * To capture more "clicks", the pseudo click-handler uses mousedown + mouseup events.
 * This is not industry standard and is vulnerable to false positives (e.g., drag events).
 *
 * @param configuration The link tracking configuration to use for the new click handlers
 * @param trackers List of tracker IDs that should track the click events
 */
function enableLinkClickTracking(configuration, trackers) {
    if (configuration === void 0) { configuration = {}; }
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    // remove listeners in case pseudoclick support has been toggled, which may duplicate handlers
    disableLinkClickTracking(trackers);
    trackers.forEach(function (id) {
        if (_trackers[id]) {
            configureLinkClickTracking(configuration, id);
            addClickListeners(id);
        }
    });
}
/**
 * Disable link click tracking.
 *
 * Removes all document-level click event handlers installed by the plugin for
 * provided tracker instances.
 *
 * @param trackers The tracker identifiers that will have their listeners removed
 */
function disableLinkClickTracking(trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    trackers.forEach(function (id) {
        if (_trackers[id] && _listeners[id]) {
            // remove all possible cases where the handler may have been attached
            window.removeEventListener('click', _listeners[id], true);
            window.removeEventListener('mouseup', _listeners[id], true);
            window.removeEventListener('mousedown', _listeners[id], true);
        }
    });
}
/**
 * Add click event listeners to links which have been added to the page since the
 * last time `enableLinkClickTracking` or `refreshLinkClickTracking` was called.
 *
 * @deprecated v4.0 moved to event delegation and this is no longer required
 * @param trackers The tracker identifiers which the have their link click state refreshed
 */
function refreshLinkClickTracking(_trackers) {
    _logger === null || _logger === void 0 ? void 0 : _logger.warn('refreshLinkClickTracking is deprecated in v4 and has no effect');
}
/**
 * Manually log a click.
 *
 * @param event The event or element information
 * @param trackers The tracker identifiers which the event will be sent to
 */
function trackLinkClick(event, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        var _a, _b, _c;
        var payload;
        if ('element' in event) {
            var includeContent = (_c = (_a = event.trackContent) !== null && _a !== void 0 ? _a : (_b = _configuration[t.id]) === null || _b === void 0 ? void 0 : _b.linkTrackingContent) !== null && _c !== void 0 ? _c : false;
            payload = processClick(event.element, includeContent);
        }
        else {
            payload = buildLinkClick(event);
        }
        if (payload)
            t.core.track(payload, event.context, event.timestamp);
    });
    flushPendingCookies();
}
/**
 * Process a clicked element into a link_click event payload.
 *
 * In case the href of the element is empty, "about:invalid" is used as the target URL.
 *
 * @param sourceElement The trackable element to be used to build the payload
 * @param includeContent Whether to include the element's contents in the payload
 * @returns A link_click SDE payload for the given element, or nothing if the element shouldn't be tracked
 */
function processClick(sourceElement, includeContent) {
    if (includeContent === void 0) { includeContent = false; }
    var elementId, elementClasses, elementTarget, elementContent;
    var anchorElement = sourceElement;
    // browsers, such as Safari, don't downcase hostname and href
    var originalSourceHostName = anchorElement.hostname || getHostName(anchorElement.href);
    var targetUrl = anchorElement.href.replace(originalSourceHostName, function (s) { return s.toLowerCase(); });
    // Ignore script pseudo-protocol links
    if (!ELEMENT_PROTOCOL_FILTER.test(targetUrl)) {
        elementId = anchorElement.id;
        elementClasses = getCssClasses(anchorElement);
        elementTarget = anchorElement.target;
        elementContent = includeContent ? anchorElement.innerHTML : undefined;
        if (!targetUrl) {
            _logger === null || _logger === void 0 ? void 0 : _logger.warn('Link click target URL empty', anchorElement);
        }
        // decodeUrl %xx
        return buildLinkClick({
            targetUrl: targetUrl || 'about:invalid',
            elementId: elementId,
            elementClasses: elementClasses,
            elementTarget: elementTarget,
            elementContent: elementContent,
        });
    }
    return;
}
/**
 * Find the nearest trackable element to the given element; this is Element.closest()
 * with a polyfill if required.
 *
 * @param target
 * @returns An element that may be used to track a link click event, or null if `target` has no eligible parent
 */
function findNearestEligibleElement(target) {
    if (target instanceof Element) {
        if (typeof target['closest'] === 'function')
            return target.closest(TRACKABLE_ELEMENTS_SELECTOR);
        var sourceElement = target;
        while (sourceElement) {
            var tagName = sourceElement.tagName.toLowerCase();
            if (TRACKABLE_ELEMENTS.indexOf(tagName) !== -1)
                return sourceElement;
            sourceElement = sourceElement.parentElement;
        }
    }
    return null;
}
/**
 * Handle a (pseudo)click event; decide if the click is for a valid, unfiltered,
 * element and track the click.
 *
 * @param tracker Tracker ID to generate the event for
 * @param evt The DOM click event itself
 */
function clickHandler(tracker, evt) {
    var context = _configuration[tracker].linkTrackingContext;
    var filter = _configuration[tracker].linkTrackingFilter;
    var event = evt || window.event;
    var button = event.which || event.button;
    var clicked = event.composed ? event.composedPath()[0] : event.target || event.srcElement;
    var target = findNearestEligibleElement(clicked);
    if (!target || target.href == null)
        return;
    if (filter && !filter(target))
        return;
    // Using evt.type (added in IE4), we avoid defining separate handlers for mouseup and mousedown.
    if (event.type === 'click') {
        trackLinkClick({
            element: target,
            context: resolveDynamicContext(context, target),
        }, [tracker]);
    }
    else if (event.type === 'mousedown') {
        if (button === 1 || button === 2) {
            _configuration[tracker].lastButton = button;
            _configuration[tracker].lastTarget = target;
        }
        else {
            delete _configuration[tracker].lastButton;
        }
    }
    else if (event.type === 'mouseup') {
        if (button === _configuration[tracker].lastButton && target === _configuration[tracker].lastTarget) {
            trackLinkClick({
                element: target,
                context: resolveDynamicContext(context, target),
            }, [tracker]);
        }
        delete _configuration[tracker].lastButton;
        delete _configuration[tracker].lastTarget;
    }
}
/**
 * Update the link-tracking configuration for the given tracker ID.
 *
 * @param param0 The new link-tracking configuration
 * @param tracker The tracker ID to update configuration for
 */
function configureLinkClickTracking(_a, tracker) {
    var _b = _a === void 0 ? {} : _a, options = _b.options, pseudoClicks = _b.pseudoClicks, trackContent = _b.trackContent, context = _b.context;
    _configuration[tracker] = {
        linkTrackingContent: trackContent,
        linkTrackingContext: context,
        linkTrackingPseudoClicks: pseudoClicks,
        linkTrackingFilter: getFilterByClass(options),
    };
}
/**
 * Add (psuedo)click handlers to the window for the given tracker ID.
 *
 * @param trackerId Tracker ID to install a listener for
 */
function addClickListeners(trackerId) {
    // by re-using exact function references the browser will prevent dupes and allow removal
    _listeners[trackerId] = _listeners[trackerId] || clickHandler.bind(null, trackerId);
    if (_configuration[trackerId].linkTrackingPseudoClicks) {
        // for simplicity and performance, we ignore drag events
        addEventListener(window, 'mouseup', _listeners[trackerId], true);
        addEventListener(window, 'mousedown', _listeners[trackerId], true);
    }
    else {
        addEventListener(window, 'click', _listeners[trackerId], true);
    }
}

export { LinkClickTrackingPlugin, disableLinkClickTracking, enableLinkClickTracking, refreshLinkClickTracking, trackLinkClick };
//# sourceMappingURL=index.module.js.map
